import { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import { Button } from "./ui/button";
import { Card } from "./ui/card";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { CATEGORIES, CATEGORY_LABEL } from "../lib/labels";
import { ChatCircleDots, PaperPlaneRight, X, Robot, User as UserIcon, Ticket, BookOpen, CheckCircle } from "@phosphor-icons/react";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { toast } from "sonner";

export default function ChatWidget({ onEscalate }) {
  const [open, setOpen] = useState(false);
  const [category, setCategory] = useState("autre");
  const [session, setSession] = useState(null);
  const [messages, setMessages] = useState([
    { role: "assistant", full: "Bonjour ! Je suis votre Assistant IA. Choisissez une catégorie et posez-moi votre question — je réponds tout de suite, et je passe le relais à un technicien si besoin.", content: "" },
  ]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [suggestTicket, setSuggestTicket] = useState(false);
  const [kbArticles, setKbArticles] = useState([]);
  const [showKb, setShowKb] = useState(false);
  const [ticketStep, setTicketStep] = useState(null); // null | "form" | "sending" | "success"
  const [ticketForm, setTicketForm] = useState({ client_name: "", client_email: "", client_phone: "", subject: "" });
  const [createdTicket, setCreatedTicket] = useState(null);
  const listRef = useRef();
  const audioCtxRef = useRef();

  const beep = () => {
    try {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (!AC) return;
      const ctx = audioCtxRef.current || new AC();
      audioCtxRef.current = ctx;
      const o = ctx.createOscillator(); const g = ctx.createGain();
      o.type = "sine"; o.frequency.value = 780;
      g.gain.setValueAtTime(0, ctx.currentTime);
      g.gain.linearRampToValueAtTime(0.06, ctx.currentTime + 0.02);
      g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.25);
      o.connect(g); g.connect(ctx.destination);
      o.start(); o.stop(ctx.currentTime + 0.25);
    } catch { /* ignore */ }
  };

  // Typewriter effect — reveal the "full" text character-by-character
  const streamInto = (index, full) => {
    return new Promise((resolve) => {
      let i = 0;
      const step = () => {
        i = Math.min(full.length, i + Math.max(1, Math.floor(full.length / 60)));
        setMessages((m) => {
          const arr = [...m];
          if (arr[index]) arr[index] = { ...arr[index], content: full.slice(0, i) };
          return arr;
        });
        listRef.current?.scrollTo?.({ top: listRef.current.scrollHeight, behavior: "smooth" });
        if (i < full.length) setTimeout(step, 15);
        else resolve();
      };
      step();
    });
  };

  // On mount: reveal initial greeting
  useEffect(() => {
    if (!open) return;
    setMessages((m) => {
      const arr = [...m];
      if (arr[0] && !arr[0].content) return arr;
      return arr;
    });
    if (messages[0].full && !messages[0].content) streamInto(0, messages[0].full);
    // eslint-disable-next-line
  }, [open]);

  useEffect(() => {
    listRef.current?.scrollTo?.({ top: listRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, open]);

  // Detect whether the user is asking to open a ticket directly (without clicking the button).
  // Two heuristics:
  //   1. The message contains explicit ticket-opening intent (e.g., "crée un ticket", "ouvre un ticket")
  //   2. The last assistant message offered to create a ticket AND the user replied affirmatively (e.g., "oui", "ok")
  const detectsTicketIntent = (userMsg) => {
    const m = (userMsg || "").toLowerCase().trim();
    if (!m) return false;
    // Explicit intent
    const explicit = /\b(cr[ée]{1,3}[rz]?|ouvr[eirz]{1,3}|d[ée]pos[eé]r?|ouverture)\b[^.!?]*\bticket/i.test(userMsg)
      || /\bticket\b[^.!?]*\b(cr[ée]|ouvr|d[ée]pos)/i.test(userMsg)
      || /\bje\s+veux\s+(un\s+)?ticket\b/i.test(userMsg)
      || /\boui[,!\s]*(cr[ée]|ouvr|allez|allez-y|s'il te pla[iî]t|stp|svp)/i.test(userMsg);
    if (explicit) return true;
    // Affirmative reply right after the Assistant IA offered a ticket
    const lastAsst = [...messages].reverse().find((x) => x.role === "assistant");
    const asstText = (lastAsst?.content || lastAsst?.full || "").toLowerCase();
    const asstOffered = asstText.includes("ticket") && (
      asstText.includes("cr[ée]er un ticket") || asstText.includes("créer un ticket") ||
      asstText.includes("ouvrir un ticket") || asstText.includes("créer un") ||
      asstText.includes("technicien")
    );
    if (asstOffered) {
      const affirm = /^\s*(oui|ok|d['’]accord|yes|volontiers|carr[ée]ment|allez-y|vas-y|allons-y|s'il te pla[iî]t|stp|svp|je veux bien)\b/i;
      if (affirm.test(userMsg)) return true;
    }
    return false;
  };

  const send = async (e) => {
    e?.preventDefault?.();
    const msg = input.trim();
    if (!msg || sending) return;
    setMessages((m) => [...m, { role: "user", content: msg }]);
    setInput("");

    // Short-circuit: user asked to open a ticket directly → open the mini-form
    if (detectsTicketIntent(msg)) {
      setMessages((m) => [...m, {
        role: "assistant",
        content: "Parfait — je vous prépare le formulaire pour créer votre ticket. Renseignez juste votre nom, votre email et un téléphone pour être joint 👇",
        streaming: false,
      }]);
      // Slight delay so the message is visible before the form pops in
      setTimeout(() => escalate(), 250);
      return;
    }

    setSending(true);
    // Placeholder assistant bubble that we will stream into
    const asstIdx = messages.length + 1;
    setMessages((m) => [...m, { role: "assistant", content: "", streaming: true }]);
    try {
      const API_BASE = (await import("../lib/api")).API_BASE;
      const res = await fetch(`${API_BASE}/chat/stream`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: session, message: msg, category }),
      });
      if (!res.ok || !res.body) throw new Error(`HTTP ${res.status}`);
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      let full = "";
      let firstDelta = true;
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        let idx;
        while ((idx = buf.indexOf("\n\n")) !== -1) {
          const line = buf.slice(0, idx).trim();
          buf = buf.slice(idx + 2);
          if (!line.startsWith("data:")) continue;
          const payload = line.slice(5).trim();
          try {
            const j = JSON.parse(payload);
            if (j.ping) continue;
            if (j.delta) {
              full += j.delta;
              if (firstDelta) { beep(); firstDelta = false; }
              setMessages((m) => {
                const arr = [...m];
                if (arr[asstIdx]) arr[asstIdx] = { ...arr[asstIdx], content: full };
                return arr;
              });
              listRef.current?.scrollTo?.({ top: listRef.current.scrollHeight });
            }
            if (j.done) {
              setSession(j.session_id);
              setSuggestTicket(!!j.suggest_ticket);
              setMessages((m) => {
                const arr = [...m];
                if (arr[asstIdx]) arr[asstIdx] = { ...arr[asstIdx], kb: j.kb_used, streaming: false };
                return arr;
              });
              if (j.kb_used?.length) {
                setKbArticles((prev) => {
                  const merged = [...j.kb_used];
                  for (const p of prev) if (!merged.find((x) => x.id === p.id)) merged.push(p);
                  return merged.slice(0, 6);
                });
                setShowKb(true);
              }
            }
            if (j.error) throw new Error(j.error);
          } catch { /* keep going */ }
        }
      }
    } catch (err) {
      setMessages((m) => {
        const arr = [...m];
        if (arr[asstIdx]) arr[asstIdx] = { role: "assistant", content: "Désolé, une erreur est survenue. Vous pouvez créer un ticket directement.", streaming: false };
        return arr;
      });
      setSuggestTicket(true);
    } finally { setSending(false); }
  };

  const escalate = () => {
    // Open the inline mini-form (nom + email + téléphone + sujet) inside the chat
    const firstUserMsg = messages.find((m) => m.role === "user")?.content || "";
    setTicketForm({
      client_name: "",
      client_email: "",
      client_phone: "",
      subject: firstUserMsg.slice(0, 100),
    });
    setTicketStep("form");
  };

  const buildTranscript = () => messages
    .filter((m) => m.content || m.full)
    .map((m) => (m.role === "user" ? "Client" : "Assistant IA") + " : " + (m.content || m.full))
    .join("\n\n");

  const submitTicket = async (e) => {
    e?.preventDefault?.();
    const name = ticketForm.client_name.trim();
    const email = ticketForm.client_email.trim();
    const phone = ticketForm.client_phone.trim();
    const subject = (ticketForm.subject.trim() || "Demande via l'Assistant IA").slice(0, 150);
    if (!name || !email || !phone) { toast.error("Nom, email et téléphone requis"); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { toast.error("Email invalide"); return; }
    setTicketStep("sending");
    const transcript = buildTranscript();
    const description = `<p><em>Conversation avec l'Assistant IA :</em></p>`
      + `<pre style="white-space:pre-wrap;font-family:inherit;font-size:0.9em;background:#f8fafc;padding:10px;border-left:3px solid #1e40af;border-radius:4px;">${transcript.replace(/</g, "&lt;")}</pre>`;
    try {
      const r = await api.post("/tickets/public", {
        client_name: name,
        client_email: email,
        client_phone: phone,
        subject,
        description,
        category,
        priority: "normale",
      });
      setCreatedTicket({ number: r.data.number, id: r.data.id });
      if (session) { api.post(`/chat/${session}/mark-escalated`, { ticket_id: r.data.id }).catch(() => null); }
      setTicketStep("success");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Impossible de créer le ticket");
      setTicketStep("form");
    }
  };

  const cancelTicketForm = () => setTicketStep(null);

  return (
    <>
      {!open && (
        <button
          onClick={() => setOpen(true)}
          data-testid="chat-open-btn"
          className="fixed bottom-5 right-5 z-40 h-14 w-14 rounded-full shadow-lg bg-primary text-primary-foreground flex items-center justify-center hover:scale-105 transition"
          aria-label="Ouvrir le chat">
          <ChatCircleDots size={26} weight="duotone"/>
          <span className="absolute -top-1 -right-1 w-3 h-3 rounded-full bg-emerald-400 border-2 border-white animate-pulse"></span>
        </button>
      )}
      {open && (
        <div className="fixed bottom-5 right-5 z-50 flex items-end gap-3" style={{ maxWidth: "min(92vw, 700px)" }}>
          {/* KB sidebar (desktop-only) */}
          {showKb && kbArticles.length > 0 && (
            <Card className="hidden md:flex flex-col w-64 shadow-2xl overflow-hidden" style={{ height: "min(600px, 80vh)" }}>
              <div className="p-3 border-b flex items-center gap-2">
                <BookOpen size={16} className="text-primary"/>
                <div className="font-medium text-sm">Articles utiles</div>
                <button onClick={() => setShowKb(false)} className="ml-auto text-muted-foreground hover:text-foreground" aria-label="Fermer">
                  <X size={14}/>
                </button>
              </div>
              <div className="flex-1 overflow-y-auto p-2 space-y-2" data-testid="chat-kb-sidebar">
                {kbArticles.map((a) => (
                  <a key={a.id} href={`/kb/${a.id}`} target="_blank" rel="noopener noreferrer"
                     data-testid={`chat-kb-${a.id}`}
                     className="block p-2.5 rounded-lg border hover:border-primary hover:bg-primary/5 transition text-xs leading-relaxed">
                    <div className="font-medium line-clamp-2">{a.title}</div>
                    <div className="text-primary text-[0.65rem] uppercase tracking-wider mt-1">Ouvrir l&apos;article →</div>
                  </a>
                ))}
              </div>
              <div className="px-3 py-2 border-t text-[0.65rem] text-muted-foreground">
                Suggéré par l'Assistant IA à partir de la catégorie choisie.
              </div>
            </Card>
          )}

          {/* Chat panel */}
          <Card className="w-[92vw] max-w-[380px] shadow-2xl flex flex-col overflow-hidden" style={{ height: "min(600px, 80vh)" }}>
            <div className="bg-primary text-primary-foreground p-3 flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-white/15 flex items-center justify-center">
                <Robot size={18} weight="duotone"/>
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold">Assistant IA</div>
                <div className="text-[0.65rem] opacity-80 uppercase tracking-widest">En ligne</div>
              </div>
              {kbArticles.length > 0 && !showKb && (
                <button onClick={() => setShowKb(true)} className="p-1.5 rounded hover:bg-white/10 hidden md:inline-flex" title="Afficher les articles KB" data-testid="chat-kb-toggle">
                  <BookOpen size={16}/>
                </button>
              )}
              <button onClick={() => setOpen(false)} className="p-1 rounded hover:bg-white/10" aria-label="Fermer" data-testid="chat-close-btn">
                <X size={18}/>
              </button>
            </div>
            <div className="px-3 py-2 border-b text-xs flex items-center gap-2">
              <span className="text-muted-foreground shrink-0">Sujet :</span>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger className="h-8 text-xs" data-testid="chat-category"><SelectValue/></SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{CATEGORY_LABEL[c]}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div ref={listRef} className="flex-1 overflow-y-auto p-3 space-y-3 bg-muted/20" data-testid="chat-messages">
              {messages.map((m, i) => (
                <div key={i} className={`flex gap-2 ${m.role === "user" ? "flex-row-reverse" : ""}`}>
                  <div className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 ${m.role === "user" ? "bg-primary/10 text-primary" : "bg-emerald-500/10 text-emerald-600"}`}>
                    {m.role === "user" ? <UserIcon size={14}/> : <Robot size={14} weight="duotone"/>}
                  </div>
                  <div className={`px-3 py-2 rounded-xl text-sm max-w-[80%] whitespace-pre-wrap leading-relaxed ${m.role === "user" ? "bg-primary text-primary-foreground rounded-tr-sm" : "bg-white border rounded-tl-sm"}`}>
                    {m.content || m.full || ""}
                    {m.role === "assistant" && (m.streaming || (m.full && m.content !== m.full)) && (
                      <span className="inline-block w-1.5 h-3.5 ml-0.5 -mb-0.5 bg-current opacity-60 animate-pulse"></span>
                    )}
                    {m.kb && m.kb.length > 0 && (
                      <div className="mt-2 pt-2 border-t border-black/10 text-[0.65rem] uppercase tracking-wider opacity-60">
                        Sources : {m.kb.map((k) => k.title).join(" · ")}
                      </div>
                    )}
                  </div>
                </div>
              ))}
              {sending && (
                <div className="flex items-center gap-2 text-xs text-muted-foreground pl-9">
                  <span className="inline-block w-2 h-2 bg-emerald-500 rounded-full animate-pulse"></span>
                  L'Assistant IA rédige…
                </div>
              )}
            </div>
            {suggestTicket && !ticketStep && (
              <div className="px-3 py-2 border-t bg-orange-50 dark:bg-orange-950/20 flex items-center justify-between gap-2">
                <div className="text-xs text-orange-900 dark:text-orange-200">Besoin d&apos;un vrai humain ?</div>
                <Button size="sm" onClick={escalate} data-testid="chat-escalate-btn">
                  <Ticket size={14} className="mr-1.5"/> Créer un ticket
                </Button>
              </div>
            )}
            {!suggestTicket && !ticketStep && messages.length > 1 && (
              <div className="px-3 py-1.5 border-t bg-muted/40 flex items-center justify-between gap-2">
                <div className="text-[0.7rem] text-muted-foreground">Envie d&apos;ouvrir un ticket directement ?</div>
                <button
                  type="button"
                  onClick={escalate}
                  data-testid="chat-escalate-btn-always"
                  className="text-xs text-primary hover:underline inline-flex items-center gap-1"
                >
                  <Ticket size={12}/> Créer un ticket
                </button>
              </div>
            )}

            {ticketStep === "form" && (
              <form onSubmit={submitTicket} className="p-3 border-t bg-blue-50 dark:bg-blue-950/20 space-y-2" data-testid="chat-ticket-form">
                <div className="text-xs font-medium text-blue-900 dark:text-blue-200 mb-1 flex items-center gap-1.5">
                  <Ticket size={13}/> Vos coordonnées pour ouvrir le ticket
                </div>
                <div>
                  <Label className="text-[0.7rem]">Nom complet *</Label>
                  <Input required autoFocus className="h-8 text-sm"
                    data-testid="chat-ticket-name"
                    value={ticketForm.client_name}
                    onChange={(e) => setTicketForm({...ticketForm, client_name: e.target.value})}
                    placeholder="Ex. Jean Dupont"/>
                </div>
                <div>
                  <Label className="text-[0.7rem]">Adresse email *</Label>
                  <Input required type="email" className="h-8 text-sm"
                    data-testid="chat-ticket-email"
                    value={ticketForm.client_email}
                    onChange={(e) => setTicketForm({...ticketForm, client_email: e.target.value})}
                    placeholder="vous@entreprise.fr"/>
                </div>
                <div>
                  <Label className="text-[0.7rem]">Téléphone *</Label>
                  <Input required type="tel" className="h-8 text-sm"
                    data-testid="chat-ticket-phone"
                    value={ticketForm.client_phone}
                    onChange={(e) => setTicketForm({...ticketForm, client_phone: e.target.value})}
                    placeholder="Ex. 04 74 12 34 56"/>
                </div>
                <div>
                  <Label className="text-[0.7rem]">Sujet</Label>
                  <Input className="h-8 text-sm"
                    data-testid="chat-ticket-subject"
                    value={ticketForm.subject}
                    onChange={(e) => setTicketForm({...ticketForm, subject: e.target.value})}
                    placeholder="Résumé en une phrase"/>
                </div>
                <div className="flex gap-2 pt-1">
                  <Button type="button" variant="secondary" size="sm" onClick={cancelTicketForm} className="flex-1" data-testid="chat-ticket-cancel">
                    Annuler
                  </Button>
                  <Button type="submit" size="sm" className="flex-1" data-testid="chat-ticket-submit">
                    <Ticket size={13} className="mr-1"/> Créer
                  </Button>
                </div>
              </form>
            )}

            {ticketStep === "sending" && (
              <div className="p-4 border-t bg-blue-50 dark:bg-blue-950/20 flex items-center gap-2 text-sm">
                <span className="inline-block w-2 h-2 bg-blue-500 rounded-full animate-pulse"></span>
                <span className="text-blue-900 dark:text-blue-200">Création du ticket en cours…</span>
              </div>
            )}

            {ticketStep === "success" && createdTicket && (
              <div className="p-4 border-t bg-emerald-50 dark:bg-emerald-950/30" data-testid="chat-ticket-success">
                <div className="flex items-start gap-2">
                  <CheckCircle size={20} weight="fill" className="text-emerald-600 shrink-0 mt-0.5"/>
                  <div className="text-sm flex-1">
                    <div className="font-semibold text-emerald-900 dark:text-emerald-200">
                      Ticket #{createdTicket.number} créé !
                    </div>
                    <div className="text-xs text-emerald-800 dark:text-emerald-300 mt-1">
                      Un email de confirmation vous a été envoyé. Un technicien reviendra vers vous rapidement.
                    </div>
                  </div>
                </div>
              </div>
            )}
            <form onSubmit={send} className="p-2 border-t flex items-end gap-2">
              <Textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                placeholder="Décrivez votre problème…"
                rows={2} className="text-sm resize-none" data-testid="chat-input"
              />
              <Button type="submit" size="sm" disabled={sending || !input.trim()} data-testid="chat-send-btn">
                <PaperPlaneRight size={16}/>
              </Button>
            </form>
          </Card>
        </div>
      )}
    </>
  );
}
